'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Fuse from 'fuse.js'
import { Search, X } from 'lucide-react'

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { track } from '@/lib/analytics/track'
import type {
  GptImage2Category,
  GptImage2Prompt,
} from '@/content/marketing/gpt-image-2-prompts/prompts'
import { PromptCard } from './prompt-card'

interface PromptItem extends GptImage2Prompt {
  imageExt: 'jpg' | 'png' | 'webp'
}

const PROMPTS_PER_PAGE = 12

type FilterValue = GptImage2Category | 'all'

interface CategoryOption {
  label: string
  value: FilterValue
  emoji: string
}

function getPageNumbers(
  current: number,
  total: number,
): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages: (number | 'ellipsis')[] = [1]
  if (current > 3) pages.push('ellipsis')
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  if (current < total - 2) pages.push('ellipsis')
  pages.push(total)
  return pages
}

interface Props {
  prompts: PromptItem[]
  categories: CategoryOption[]
}

export function PromptsListing({ prompts, categories }: Props) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const activeCategory = (searchParams.get('category') ||
    'all') as FilterValue
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  const [query, setQuery] = useState(searchParams.get('q') || '')

  const searchTrackTimeout = useRef<ReturnType<typeof setTimeout>>(null)
  const filteredRef = useRef<PromptItem[]>([])

  const fuse = useMemo(
    () =>
      new Fuse(prompts, {
        keys: [
          { name: 'title', weight: 0.3 },
          { name: 'useCase', weight: 0.25 },
          { name: 'prompt', weight: 0.2 },
          { name: 'tags', weight: 0.15 },
          { name: 'category', weight: 0.1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [prompts],
  )

  const filtered = useMemo(() => {
    let result = prompts
    if (query.trim()) {
      result = fuse
        .search(query.trim())
        .map((r: { item: PromptItem }) => r.item)
    }
    if (activeCategory !== 'all') {
      result = result.filter(p => p.category === activeCategory)
    }
    return result
  }, [prompts, fuse, query, activeCategory])

  useEffect(() => {
    filteredRef.current = filtered
  }, [filtered])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PROMPTS_PER_PAGE))
  const page = Math.min(currentPage, totalPages)
  const paginated = filtered.slice(
    (page - 1) * PROMPTS_PER_PAGE,
    page * PROMPTS_PER_PAGE,
  )
  const pageNumbers = getPageNumbers(page, totalPages)

  function buildUrl(overrides: {
    page?: number
    category?: string
    q?: string
  }) {
    const qs = new URLSearchParams()
    const p = overrides.page ?? page
    const cat = overrides.category ?? activeCategory
    const search = overrides.q ?? query

    if (p > 1) qs.set('page', String(p))
    if (cat !== 'all') qs.set('category', cat)
    if (search.trim()) qs.set('q', search.trim())

    const s = qs.toString()
    return s ? `/prompts/gpt-image-2?${s}` : '/prompts/gpt-image-2'
  }

  function handleSearch(value: string) {
    setQuery(value)
    const qs = new URLSearchParams()
    if (activeCategory !== 'all') qs.set('category', activeCategory)
    if (value.trim()) qs.set('q', value.trim())
    const s = qs.toString()
    router.replace(s ? `/prompts/gpt-image-2?${s}` : '/prompts/gpt-image-2', {
      scroll: false,
    })

    if (searchTrackTimeout.current) clearTimeout(searchTrackTimeout.current)
    if (value.trim()) {
      searchTrackTimeout.current = setTimeout(() => {
        track('gpt_image_2_prompts_search', {
          query: value.trim(),
          result_count: filteredRef.current.length,
        })
      }, 500)
    }
  }

  function handleCategoryClick(cat: FilterValue) {
    track('gpt_image_2_prompts_category_filter', { category: cat })
    const qs = new URLSearchParams()
    if (cat !== 'all') qs.set('category', cat)
    if (query.trim()) qs.set('q', query.trim())
    const s = qs.toString()
    router.replace(s ? `/prompts/gpt-image-2?${s}` : '/prompts/gpt-image-2', {
      scroll: false,
    })
  }

  return (
    <>
      {/* Category pills */}
      <div className="mt-10 flex flex-wrap gap-3">
        {categories.map(cat => {
          const isActive = cat.value === activeCategory
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleCategoryClick(cat.value)}
              className={`flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'border-foreground bg-foreground text-background shadow-md'
                  : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="text-base">{cat.emoji}</span>
              {cat.label}
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative mt-8">
        <Search className="text-muted-foreground absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search prompts by name, use case, or style…"
          className="border-border bg-background placeholder:text-muted-foreground focus:border-foreground/30 focus:ring-foreground/10 h-11 w-full rounded-lg border pr-10 pl-11 text-sm transition-colors outline-none focus:ring-1"
        />
        {query && (
          <button
            type="button"
            onClick={() => handleSearch('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 rounded-sm p-0.5"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {query.trim() && (
        <p className="text-muted-foreground mt-4 text-sm">
          {filtered.length} {filtered.length === 1 ? 'result' : 'results'} for
          &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      {/* Masonry — CSS columns keep cards at their natural height */}
      <div className="mt-8">
        {paginated.length > 0 ? (
          <div className="columns-1 gap-6 sm:columns-2 xl:columns-3">
            {paginated.map(prompt => (
              <PromptCard
                key={prompt.slug}
                prompt={prompt}
                imageExt={prompt.imageExt}
              />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground text-lg">
              No prompts found.{' '}
              {(query.trim() || activeCategory !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('')
                    handleCategoryClick('all')
                  }}
                  className="text-foreground underline underline-offset-4 hover:no-underline"
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-12">
          <Pagination>
            <PaginationContent>
              {page > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={buildUrl({ page: page - 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('gpt_image_2_prompts_paginate', {
                        page: page - 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page - 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
              {pageNumbers.map((p, i) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href={buildUrl({ page: p })}
                      isActive={p === page}
                      onClick={e => {
                        e.preventDefault()
                        track('gpt_image_2_prompts_paginate', {
                          page: p,
                          category: activeCategory,
                          query: query.trim() || undefined,
                        })
                        router.replace(buildUrl({ page: p }), {
                          scroll: false,
                        })
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              {page < totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={buildUrl({ page: page + 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('gpt_image_2_prompts_paginate', {
                        page: page + 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page + 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}
